/**
 * forms.tsx — reusable register / login forms wired to the identity gateway.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: C1.7 (portal redesign).
 *
 * WHY: the same two forms appear on the /auth surface AND inside the checkout
 * account step (register carries a verified `reEntryToken` so the new account
 * adopts the completed assessment). Both call the AuthProvider (persisting the
 * opaque session token) and map gateway/identity failures via
 * {@link mapRegisterError} / {@link mapLoginError} so patients see actionable
 * copy instead of a generic "Please try again".
 *
 * NO RE-ASKING (checkout path only): when the journey already collected the
 * email (Step-0 Lead) and date of birth (the `date`-kind question),
 * `RegisterForm` receives them as `prefillEmail`/`dateOfBirth` from the
 * server account-bootstrap — both fields render locked (read-only); only
 * password is user-entered. Missing values fall back to editable inputs for
 * that field only. The standalone `/auth?mode=register` path passes neither
 * prop. The `RegisterRequest` wire contract:
 * `{email, password, dateOfBirth, reEntryToken?}` (identity ignores browser
 * email/DOB when the token is present and resolves them server-side from it).
 *
 * `onSuccess` receives the freshly signed-in principal (role included) so a
 * caller can route by role — AuthPage does; CheckoutFlow ignores the argument
 * and just advances its own stage, which TS allows (a callback may declare
 * fewer params than it's given).
 *
 * THE CHECKOUT VARIANT (CTO 2026-09-24): *"Do we need to show the date of
 * birth here? It should just be 'Enter the password' and 'Re-enter the
 * password.' Then match the passwords entered, and then enter the code."*
 * `RegisterForm variant="checkout"` is the post-pay account step's shape and
 * NOTHING ELSE changes it — the standalone `/auth` register passes no variant
 * and renders exactly what it did (one password, editable email, DOB picker, a
 * 409 painted as "already has an account"). What the variant changes:
 *  - a KNOWN email is a quiet "Creating your account for <email>" line (plus a
 *    hidden username field for password managers), never an editable box;
 *  - a KNOWN date of birth is not shown at all — and is still SENT, exactly as
 *    before (the register body is built from `effectiveDob` either way). An
 *    UNKNOWN one still renders the picker: the form never invents a DOB and
 *    never registers without one;
 *  - a "Re-enter your password" box, checked against the first on submit
 *    BEFORE anything leaves the browser (so a mismatch never reveals the code
 *    gate). Its value is never put on any wire — the register body is built
 *    field by field below and names only `password`;
 *  - identity's register 409 ("An account with this email already exists")
 *    goes to `onExistingAccount` instead of being painted as an error, so the
 *    caller can switch to sign-in in place.
 *
 * `LoginForm lockedEmail` is the other half: the sign-in view a returning
 * patient is switched to, with the address they started with locked.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { ApiError } from '../api/client';
import { trackSignUp } from '../tracking/dataLayer';
import { DatePicker, displayDate, todayIso } from '../ui/DatePicker';
import { Button, ErrorLine, Field, PasswordInput, TextInput } from '../ui/kit';
import { useAuth } from './AuthContext';
import type { Me } from '../api/types';
import {
  MAX_PASSWORD,
  MIN_PASSWORD,
  isValidEmail,
  mapLoginError,
  mapRegisterError,
} from './registerErrors';

export { MIN_PASSWORD, MAX_PASSWORD };

/** The visible sentence when the checkout confirm box does not match the password. */
export const PASSWORD_MISMATCH_MESSAGE = 'The passwords don’t match. Enter the same password in both boxes.';
/** The visible sentence when the checkout confirm box is left empty. */
export const PASSWORD_CONFIRM_EMPTY_MESSAGE = 'Re-enter your password.';

/**
 * The quiet line that replaces the locked email box on the checkout register:
 * the patient sees WHICH address the account is for and cannot edit it (identity
 * resolves the address from the assessment whatever the browser sends).
 */
export function creatingAccountFor(email: string): string {
  return `Creating your account for ${email}`;
}

/**
 * What a checkout register tells its caller when identity answers 409.
 *
 * `spentToken` is the re-entry token the failed register CARRIED. identity
 * redeems (single-use) that token BEFORE it discovers the address is taken, so
 * by the time the 409 arrives it is burned — a sign-in that tried to bind the
 * order with it would be refused. The caller needs to know which token not to
 * reuse; see `CheckoutFlow`'s `bindGrantToken`.
 */
export interface ExistingAccountConflict {
  email: string;
  spentToken?: string;
}

/** Ids the checkout variant ties together with `aria-describedby`. */
const PASSWORD_HINT_ID = 'reg-password-hint';
const PASSWORD_ERROR_ID = 'reg-password-error';
const CONFIRM_ID = 'reg-password-confirm';
const CONFIRM_ERROR_ID = 'reg-password-confirm-error';

/**
 * Register form. `reEntryToken` links a guest assessment to the new account.
 * `prefillEmail`/`dateOfBirth` — when provided (the checkout path, once the
 * journey already knows both) — lock the email field and drop the DOB field;
 * absent (the standalone `/auth?mode=register` path), both stay editable.
 */
/**
 * RegisterForm — Create your account.
 *
 * Assessment-backed journeys pass `prefillEmail` + `dateOfBirth` from the
 * server account-bootstrap (or in-session continuity). Those fields render
 * read-only; only password is user-entered. Missing values fall back to
 * editable inputs for that field only. `variant="checkout"` goes further — see
 * the file header.
 */
export function RegisterForm({
  reEntryToken,
  prefillEmail,
  dateOfBirth,
  leadId,
  resolveToken,
  onSuccess,
  variant = 'standalone',
  onExistingAccount,
}: {
  /** Option-C assessment bind — a verified re-entry token (see verifyRestoreCode). */
  reEntryToken?: string;
  prefillEmail?: string;
  dateOfBirth?: string;
  /**
   * Acquisition-funnel lead id. Present on the checkout path only — fires C6
   * `sign_up` after a successful register. Absent on standalone `/auth` (that
   * path is untracked / reserved).
   */
  leadId?: string;
  /**
   * Option C, form-first checkout: when no `reEntryToken` is in hand yet,
   * this is awaited on submit (after local validation) to mint one — the
   * checkout account step reveals the inline email-code gate here and resolves
   * with the verified token, or `null` if the visitor backs out. Absent on the
   * standalone /auth path and the return-visit (code-first) path, where the
   * token is already known before the form renders.
   */
  resolveToken?: () => Promise<string | null>;
  onSuccess: (principal: Me) => void;
  /**
   * `'checkout'` — the post-pay account step (CTO 2026-09-24): known email as a
   * quiet line, known DOB hidden (still sent), a "Re-enter your password" box.
   * Default `'standalone'` renders exactly what `/auth` always has.
   */
  variant?: 'standalone' | 'checkout';
  /**
   * identity answered the register 409 — an account already holds this
   * address. When set, the conflict is handed here INSTEAD of being painted as
   * a form error (the checkout step switches to sign-in in place). Absent (the
   * standalone `/auth`), the 409 keeps its "already has an account" line.
   */
  onExistingAccount?: (conflict: ExistingAccountConflict) => void;
}) {
  const { register } = useAuth();
  const checkout = variant === 'checkout';
  const [email, setEmail] = useState(prefillEmail ?? '');
  const [password, setPassword] = useState('');
  // CHECKOUT ONLY, and deliberately never read by the register call below: the
  // body is built field by field and names `password` alone.
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [dobInput, setDobInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Bootstrap can resolve after first paint — sync locked values when props arrive.
  useEffect(() => {
    if (prefillEmail) setEmail(prefillEmail);
  }, [prefillEmail]);

  const emailLocked = Boolean(prefillEmail);
  const dobKnown = Boolean(dateOfBirth);
  const effectiveDob = dateOfBirth ?? dobInput;

  /**
   * Checkout: a failed check also puts the keyboard on the field it is about.
   * The error line is `role="alert"` either way; focus is what stops a keyboard
   * user having to hunt back up the form for the box to fix. The standalone form
   * keeps its old behaviour (announce only).
   */
  function refuse(errors: Record<string, string>, fieldId: string) {
    setFieldErrors(errors);
    if (checkout) document.getElementById(fieldId)?.focus();
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    setError(null);
    setFieldErrors({});

    const trimmedEmail = email.trim();
    if (!isValidEmail(trimmedEmail)) {
      refuse({ email: 'Enter a valid email address.' }, 'reg-email');
      return;
    }
    if (password.length < MIN_PASSWORD) {
      refuse({ password: `Password must contain at least ${MIN_PASSWORD} characters.` }, 'reg-password');
      return;
    }
    if (password.length > MAX_PASSWORD) {
      refuse({ password: `Password must be at most ${MAX_PASSWORD} characters.` }, 'reg-password');
      return;
    }
    if (password.trim().length === 0) {
      refuse({ password: 'Enter a password.' }, 'reg-password');
      return;
    }
    // THE MATCH CHECK RUNS BEFORE ANYTHING LEAVES THE BROWSER — before the
    // form-first lane reveals the code gate, and before register. "Match the
    // passwords entered, and then enter the code" (CTO), in that order.
    if (checkout && passwordConfirm !== password) {
      refuse(
        { passwordConfirm: passwordConfirm ? PASSWORD_MISMATCH_MESSAGE : PASSWORD_CONFIRM_EMPTY_MESSAGE },
        CONFIRM_ID,
      );
      return;
    }
    if (!effectiveDob) {
      refuse({ dateOfBirth: 'Enter your date of birth.' }, 'reg-dob');
      return;
    }

    setBusy(true);
    // Hoisted out of the try: a 409 has to report WHICH token it spent.
    let token = reEntryToken;
    try {
      // Form-first checkout: mint the re-entry token now (reveals the inline
      // code gate) if we don't already have one. A null result means the visitor
      // backed out of the gate — stop without registering, form stays editable.
      if (!token && resolveToken) {
        token = (await resolveToken()) ?? undefined;
        if (!token) {
          setBusy(false);
          return;
        }
      }
      const principal = await register({
        email: trimmedEmail,
        password,
        dateOfBirth: effectiveDob,
        ...(token ? { reEntryToken: token } : {}),
      });
      onSuccess(principal);
      // C6 — sign_up AFTER onSuccess so a GTM throw cannot land in this catch
      // and paint a register failure on an account that WAS created. The opaque
      // leadId is the ONLY thing that goes to the dataLayer: `trimmedEmail` is
      // in scope right here, and it must stay out of a page-global array that
      // an externally-administered container can read.
      if (leadId) {
        trackSignUp({ leadId });
      }
    } catch (err) {
      // An account already holds this address. identity's register has two 409s
      // and both mean that (the address is taken, or the assessment is already
      // bound to an account — which carries the same address); keyed on the
      // STATUS, not the sentence, so a reworded message cannot silently strand a
      // returning patient on an error with no sign-in control beside it.
      if (onExistingAccount && err instanceof ApiError && err.status === 409) {
        onExistingAccount({ email: trimmedEmail, spentToken: token });
        return;
      }
      const mapped = mapRegisterError(err);
      if (mapped.fieldErrors && Object.keys(mapped.fieldErrors).length > 0) {
        setFieldErrors(mapped.fieldErrors);
      }
      if (mapped.formError) setError(mapped.formError);
    } finally {
      setBusy(false);
    }
  }

  const passwordDescribedBy = checkout ? (fieldErrors.password ? PASSWORD_ERROR_ID : PASSWORD_HINT_ID) : undefined;
  /**
   * A field error for a box the checkout variant does not render (the locked
   * email is a line, a known DOB is not shown, and there is never an
   * `assessmentId` box) would otherwise have nowhere to appear — a refusal the
   * patient cannot see reads as a button that does nothing. Shown form-level.
   */
  const unplacedFieldErrors = checkout
    ? [emailLocked ? fieldErrors.email : undefined, dobKnown ? fieldErrors.dateOfBirth : undefined, fieldErrors.assessmentId].filter(
        (msg): msg is string => Boolean(msg),
      )
    : [];

  return (
    <form onSubmit={handleSubmit} aria-label="Create account" noValidate>
      {checkout && emailLocked ? (
        <>
          {/* NOT A FIELD: the address is the one the questionnaire was sent
              from, and identity resolves it from the assessment whatever the
              browser says. A quiet line says which account this is; the hidden
              username input is for password managers only (it is display:none,
              so it is in no tab order and no accessibility tree). */}
          <p style={{ color: 'var(--gps-muted)', fontSize: '14.5px', margin: '0 0 var(--gps-s-3)', overflowWrap: 'anywhere' }}>
            {creatingAccountFor(email)}
          </p>
          <input type="email" name="username" autoComplete="username" value={email} readOnly hidden />
        </>
      ) : (
        <Field
          label="Email address"
          htmlFor="reg-email"
          hint={emailLocked ? 'The email you started with' : undefined}
          error={fieldErrors.email}
        >
          <TextInput
            id="reg-email"
            type="email"
            value={email}
            autoComplete="email"
            readOnly={emailLocked}
            aria-invalid={Boolean(fieldErrors.email)}
            onChange={emailLocked ? undefined : (e) => setEmail(e.target.value)}
            style={emailLocked ? { background: 'var(--gps-surface-2)', color: 'var(--gps-muted)' } : undefined}
          />
        </Field>
      )}
      <Field
        label="Password"
        htmlFor="reg-password"
        hint={`At least ${MIN_PASSWORD} characters.`}
        error={fieldErrors.password}
        hintId={checkout ? PASSWORD_HINT_ID : undefined}
        errorId={checkout ? PASSWORD_ERROR_ID : undefined}
      >
        <PasswordInput
          id="reg-password"
          value={password}
          autoComplete="new-password"
          aria-invalid={Boolean(fieldErrors.password)}
          aria-describedby={passwordDescribedBy}
          onChange={(e) => setPassword(e.target.value)}
        />
      </Field>
      {checkout ? (
        <Field
          label="Re-enter your password"
          htmlFor={CONFIRM_ID}
          error={fieldErrors.passwordConfirm}
          errorId={CONFIRM_ERROR_ID}
        >
          <PasswordInput
            id={CONFIRM_ID}
            value={passwordConfirm}
            autoComplete="new-password"
            aria-invalid={Boolean(fieldErrors.passwordConfirm)}
            aria-describedby={fieldErrors.passwordConfirm ? CONFIRM_ERROR_ID : undefined}
            onChange={(e) => setPasswordConfirm(e.target.value)}
          />
        </Field>
      ) : null}
      {dobKnown ? (
        checkout ? null : (
          <Field label="Date of birth" htmlFor="reg-dob">
            {/* SHOWN THROUGH THE SAME FORMATTER AS THE EDITABLE FIELD (2026-08-20).
                This is a plain read-only TextInput rather than a DatePicker — there
                is nothing to pick — but it is the SAME question the patient just
                answered a screen earlier in `dd/mm/yyyy`, and echoing their answer
                back as "1990-01-01" is the foreign-format confusion the UK field
                was built to remove, at the one moment they are checking we got
                their identity right. `displayDate` is display only: `effectiveDob`
                still POSTs the raw ISO the server stores, and a value that is not a
                whole ISO date (a full timestamp from bootstrap, say) is shown back
                unchanged rather than blanked. */}
            <TextInput
              id="reg-dob"
              type="text"
              value={displayDate(dateOfBirth!)}
              readOnly
              aria-readonly="true"
              style={{ background: 'var(--gps-surface-2)', color: 'var(--gps-muted)' }}
            />
          </Field>
        )
      ) : (
        <Field
          label="Date of birth"
          htmlFor="reg-dob"
          hint="You must be 18 or over."
          error={fieldErrors.dateOfBirth}
        >
          {/* openView="dob" (2026-09-04, CTO): empty, this calendar opened on
              today, so a patient born in 1994 paged back 32 years. The opening
              MONTH is all that moves — the field stays empty until they type or
              pick, and `max` still reaches today so an under-18 can enter their
              real date and get the age-gate rejection rather than being unable
              to submit at all. On the CHECKOUT variant this is the fallback for
              a lane where the DOB is genuinely unknown: never an invented date,
              never a register without one. */}
          <DatePicker id="reg-dob" value={dobInput} onChange={setDobInput} max={todayIso()} openView="dob" />
        </Field>
      )}
      {unplacedFieldErrors.map((msg) => (
        <ErrorLine key={msg}>{msg}</ErrorLine>
      ))}
      {error ? <ErrorLine>{error}</ErrorLine> : null}
      <Button type="submit" variant="cta" disabled={busy} arrow={!busy}>
        {busy ? 'Creating…' : 'Create account'}
      </Button>
    </form>
  );
}

/**
 * Login form.
 *
 * `lockedEmail` (checkout only) is the sign-in view a returning patient is
 * switched to when their register 409s: the address is the one they started
 * with, shown read-only, and only the password is theirs to type.
 * `passwordDescribedBy` names the sentence explaining WHY they are signing in,
 * so it is read with the field focus lands on. Both absent (the standalone
 * `/auth`) → exactly the form it always was.
 */
export function LoginForm({
  onSuccess,
  lockedEmail,
  passwordDescribedBy,
}: {
  onSuccess: (principal: Me) => void;
  lockedEmail?: string;
  passwordDescribedBy?: string;
}) {
  const { login } = useAuth();
  const [email, setEmail] = useState(lockedEmail ?? '');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const locked = Boolean(lockedEmail);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    setError(null);
    if (!isValidEmail(email) || !password) {
      setError(locked ? 'Enter your password.' : 'Enter your email and password.');
      return;
    }
    setBusy(true);
    try {
      const principal = await login({ email: email.trim(), password });
      onSuccess(principal);
    } catch (err) {
      const mapped = mapLoginError(err);
      setError(mapped.formError ?? "We couldn't sign you in right now. Please try again in a few minutes.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} aria-label="Sign in" noValidate>
      <Field label="Email address" htmlFor="login-email" hint={locked ? 'The email you started with' : undefined}>
        <TextInput
          id="login-email"
          type="email"
          value={email}
          autoComplete={locked ? 'username' : 'email'}
          readOnly={locked}
          onChange={locked ? undefined : (e) => setEmail(e.target.value)}
          style={locked ? { background: 'var(--gps-surface-2)', color: 'var(--gps-muted)' } : undefined}
        />
      </Field>
      <Field label="Password" htmlFor="login-password">
        <PasswordInput
          id="login-password"
          value={password}
          autoComplete="current-password"
          aria-describedby={passwordDescribedBy}
          onChange={(e) => setPassword(e.target.value)}
        />
      </Field>
      {error ? <ErrorLine>{error}</ErrorLine> : null}
      <Button type="submit" variant="cta" disabled={busy} arrow={!busy}>
        {busy ? 'Signing in…' : 'Sign in'}
      </Button>
    </form>
  );
}
